import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  extractSelectedNonHistoricalTicketFromState,
  getDecisionNodeAtPath,
  isLoadingUnique,
  getDecisionNodeChildrenPaths,
} from '../../../../extractFromState';
import { addEdgeToDecisionTreePath, returnToPreviousMacroWizardPath } from '../../../../actions/searchActions';
import { BackButton, NextButton, CommonBlueButton } from '../../../common/buttonsComponents';
import {
  Container,
  SubText,
  WarningCircle,
  StepsIndicatorBox,
  StepsIndicatorCircles,
  StepsIndicatorTextBox,
  StepsIndicatorText,
} from './DTSubmitPartnerReachOutNode.styles';
import { ActionsButtonsWrapper, Title, TopContainer } from '../../DecisionTree.styles';
import {
  isValidPartnerReachOutTicket,
  hasSimplrSubmittedReachOut,
  hasPartnerRespondedToReachOut,
  hasSimplrAgentActionedToReachOut,
  partnerReachOutRetryNeeded,
} from 'util/ticketSelector';
import { setLoadingUnique } from 'actions';
import LoadingComponent, { LoadingWrapper } from 'components/LoadingComponent/LoadingComponent';
import { createPartnerReachOut, updatePartnerReachOutStatus } from 'actions/ticketActions';
import { REACH_OUT_STATUSES } from 'constants/partnerReachOut';
import { handleError } from 'services/ErrorService/ErrorService';
import { convertStringToSnakeCase } from '../../../../util/util';

const DTSubmitPartnerReachOutNode = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => isLoadingUnique(state, 'DTSubmitPartnerReachOutNode'));
  const setLoading = (value) => setLoadingUnique(value, 'DTSubmitPartnerReachOutNode');
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const configuredEdges = useSelector(getDecisionNodeChildrenPaths);
  const ticket = useSelector(extractSelectedNonHistoricalTicketFromState);

  const ticketFields = ticket?.moreTicketInfo?.ticketFields;
  const pendingField = decisionNode?.partnerTag;
  const pendingFieldValue = useMemo(() => {
    if (!Array.isArray(ticketFields) || !pendingField) {
      return;
    }
    const ticketField = ticketFields.find((field) => field.title === pendingField);
    const value = ticketField?.value;

    return value ? convertStringToSnakeCase(value) : undefined;
  }, [pendingField, ticketFields]);

  const retryNeeded = partnerReachOutRetryNeeded(ticket);
  const isBeforeRequesting = !isValidPartnerReachOutTicket(ticket) || retryNeeded;
  const hasSimplrSubmitted = hasSimplrSubmittedReachOut(ticket);
  const hasPartnerResponded = hasPartnerRespondedToReachOut(ticket);
  const hasSimplrAgentActioned = hasSimplrAgentActionedToReachOut(ticket);

  const stage =
    (isBeforeRequesting && 'before') ||
    (hasSimplrSubmitted && 'pending') ||
    (hasPartnerResponded && 'complete') ||
    (hasSimplrAgentActioned && 'actioned');

  const getTitleOrSubtext = (fieldName) => {
    if (hasSimplrAgentActioned) {
      return fieldName === 'Title'
        ? 'Partner reach out request has already been completed by a Simplr Agent'
        : 'This request cannot be done more than once on a single ticket';
    }

    return decisionNode[`${stage}${fieldName}`];
  };

  const handleNextButton = async () => {
    dispatch(setLoading(true));
    try {
      if (!hasSimplrAgentActioned) {
        await dispatch(updatePartnerReachOutStatus(REACH_OUT_STATUSES.SIMPLR_AGENT_ACTIONED));
      }
      const nextPath = configuredEdges.includes(pendingFieldValue) ? pendingFieldValue : 'unknown_response_dont_remove';
      dispatch(addEdgeToDecisionTreePath(nextPath));
    } catch (error) {
      handleError({ error });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRequestClick = async () => {
    dispatch(setLoading(true));
    try {
      await dispatch(createPartnerReachOut());
    } catch (error) {
      dispatch(addEdgeToDecisionTreePath('request_failed'));
    }
    dispatch(setLoading(false));
  };

  return isLoading ? (
    <LoadingWrapper>
      <LoadingComponent delay={0} />
    </LoadingWrapper>
  ) : (
    <TopContainer>
      <Container>
        <WarningCircle src="images/warning-circle.svg" alt="" />
        <Title>{getTitleOrSubtext('Title')}</Title>
        <SubText>{getTitleOrSubtext('Subtext')}</SubText>
        {(hasPartnerResponded || hasSimplrAgentActioned) && (
          <SubText>{`Status of the request: ${pendingFieldValue ? pendingFieldValue : 'unknown'}`}</SubText>
        )}
        {isBeforeRequesting ? (
          <CommonBlueButton disabled={!isBeforeRequesting} active={isBeforeRequesting} onClick={handleRequestClick}>
            {decisionNode.actionButtonText}
          </CommonBlueButton>
        ) : (
          <StepsIndicatorBox>
            <StepsIndicatorCircles
              src={`images/steps-${hasPartnerResponded || hasSimplrAgentActioned ? 3 : 2}-out-of-3.svg`}
              alt=""
            />
            <StepsIndicatorTextBox>
              <StepsIndicatorText>Submitted</StepsIndicatorText>
              <StepsIndicatorText>In-progress</StepsIndicatorText>
              <StepsIndicatorText isPending={!(hasPartnerResponded || hasSimplrAgentActioned)}>
                Responded
              </StepsIndicatorText>
            </StepsIndicatorTextBox>
          </StepsIndicatorBox>
        )}
      </Container>
      <ActionsButtonsWrapper>
        <NextButton
          disabled={!(hasPartnerResponded || hasSimplrAgentActioned)}
          active={hasPartnerResponded || hasSimplrAgentActioned}
          type="submit"
          onClick={handleNextButton}
        >
          NEXT
        </NextButton>
        <BackButton onClick={() => dispatch(returnToPreviousMacroWizardPath())}>BACK</BackButton>
      </ActionsButtonsWrapper>
    </TopContainer>
  );
};

export default DTSubmitPartnerReachOutNode;
